import React, { useEffect, useState } from "react";
import { useUser } from "../context/AuthContext";
import { getUserBookings } from "../api";
import ParticleBackground from "../components/ParticleBackground";

const brand = {
  nearBlack: '#141318',
  darkGray: '#1F2937',
  light: '#E9EAE8',
  white: '#FFFFFF',
  blueGray: '#566D87',
  gold: '#FFD404',
  ink: '#00181C',
};

const BookingHistory = () => {
  const { user } = useUser();
  const [bookings, setBookings] = useState(null); // null = loading, [] = empty
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null); // For modal
  const [tab, setTab] = useState('ordered'); // 'ordered', 'delivered', 'cancelled'

  useEffect(() => {
    let mounted = true;
    setBookings(null);
    setError("");
    getUserBookings()
      .then(data => {
        if (mounted) setBookings(data);
      })
      .catch(err => {
        if (mounted) setError(err.response?.data?.message || err.message || "Error loading bookings");
        setBookings([]);
      });
    return () => { mounted = false; };
  }, []);

  // Tab config for nav
  const navTabs = [
    { key: 'ordered', label: 'Upcoming', color: 'text-yellow-400', border: 'border-yellow-400' },
    { key: 'delivered', label: 'Attended', color: 'text-green-400', border: 'border-green-400' },
    { key: 'cancelled', label: 'Cancelled', color: 'text-red-400', border: 'border-red-400' },
  ];

  return (
    <>
  <div className="relative min-h-screen flex flex-col items-center justify-center py-6 sm:py-8 md:py-12 lg:py-16 overflow-x-hidden" style={{ background: `radial-gradient(1000px 500px at 10% -10%, rgba(255, 212, 4, 0.06), transparent 70%), radial-gradient(900px 500px at 110% 10%, rgba(86, 109, 135, 0.06), transparent 70%), ${brand.nearBlack}`, color: brand.light }}>
      {/* Fondo decorativo + partículas */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <ParticleBackground className="opacity-25" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl opacity-70 animate-pulse" style={{ background: 'radial-gradient(circle at 30% 30%, rgba(255,212,4,0.18), transparent 60%)' }} />
        <div className="absolute bottom-0 right-0 w-[320px] h-[220px] rounded-full blur-2xl opacity-60 animate-pulse" style={{ background: 'radial-gradient(circle at 70% 70%, rgba(86,109,135,0.18), transparent 60%)' }} />
      </div>
      <div className="relative z-10 w-full max-w-3xl px-1 xs:px-2 sm:px-4 md:px-0">
        <div className="rounded-2xl sm:rounded-3xl shadow-2xl px-2 xs:px-4 sm:px-6 md:px-10 py-4 xs:py-6 sm:py-10 md:py-12 flex flex-col items-center backdrop-blur-xl animate-fade-in" style={{ backgroundColor: 'rgba(31,41,55,0.55)', border: `1px solid ${brand.blueGray}33` }}>
          <h1 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl font-extrabold mb-1 tracking-tight text-center" style={{ fontFamily:'Inter, Audiowide, sans-serif', color: brand.white, textShadow:'0 2px 12px rgba(0,0,0,0.5)' }}>Booking History</h1>
          <p className="text-sm xs:text-base sm:text-lg mb-4 sm:mb-6 font-medium text-center" style={{ color: brand.blueGray, textShadow:'0 2px 8px rgba(0,0,0,0.45)' }}>See all your past and upcoming bookings</p>
          <div className="w-full flex flex-col gap-3 xs:gap-4 mt-2">
            {/* Loading state */}
            {bookings === null && (
              <div className="flex justify-center items-center py-12">
                <span className="text-lg animate-pulse" style={{ color: brand.blueGray }}>Loading your bookings...</span>
              </div>
            )}
            {/* Error state */}
            {error && (
              <div className="flex justify-center items-center py-8">
                <span className="text-red-400 text-lg">{error}</span>
              </div>
            )}
            {/* Empty state */}
            {bookings && bookings.length === 0 && !error && (
              <div className="flex flex-col items-center py-12">
                <span className="text-lg mb-2" style={{ color: brand.blueGray }}>You have no bookings yet.</span>
                <span className="text-sm" style={{ color: brand.blueGray }}>Your booking history will appear here once you make a reservation.</span>
              </div>
            )}
            {/* Nav tabs for status */}
            {bookings && bookings.length > 0 && !error && (
              <>
                <div className="flex flex-wrap justify-center mb-4 sm:mb-6 gap-2 xs:gap-3">
                  {navTabs.map(tabItem => (
                    <button
                      key={tabItem.key}
                      className={`px-5 py-2 font-bold text-base rounded-t-xl border-b-4 transition-all duration-200 focus:outline-none ${tab === tabItem.key ? `text-[${brand.white}] border-[${brand.gold}]` : 'text-gray-400 border-transparent bg-transparent hover:bg-white/5'}`}
                      style={tab === tabItem.key ? { backgroundColor: 'rgba(31,41,55,0.35)', borderColor: brand.gold, color: brand.white } : {}}
                      onClick={() => setTab(tabItem.key)}
                    >
                      {tabItem.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 xs:gap-4">
                  {bookings.filter(b => b.state === tab).length === 0 ? (
                    <div className="col-span-2 flex flex-col items-center py-8 text-lg" style={{ color: brand.blueGray }}>No bookings in this category.</div>
                  ) : (
                    bookings.filter(b => b.state === tab).map(b => (
                      <div
                        key={b.id}
                        className={`cursor-pointer rounded-xl xs:rounded-2xl shadow-lg p-3 xs:p-4 sm:p-5 flex flex-col gap-2 transition-all`}
                        style={{ backgroundColor: 'rgba(20,19,24,0.8)', border: `1px solid ${brand.blueGray}33` }}
                        onClick={() => setSelected(b)}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                          <span className="font-bold text-lg" style={{ color: brand.white }}>{b.date?.split(",")[0]}</span>
                          <span className={
                            b.state === 'ordered' ? 'text-yellow-400' :
                            b.state === 'delivered' ? 'text-green-400' :
                            b.state === 'cancelled' ? 'text-red-400' : 'text-[#B6F0FF]'
                          }>
                            {b.state === 'ordered' ? 'Upcoming' : b.state === 'delivered' ? 'Attended' : b.state === 'cancelled' ? 'Cancelled' : '-'}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 xs:gap-3 text-xs xs:text-sm">
                          <span className="font-semibold" style={{ color: brand.blueGray }}>{b.tipo?.charAt(0).toUpperCase() + b.tipo?.slice(1)}</span>
                          <span style={{ color: brand.blueGray }}>
                            {b.date?.split(",")[1]?.trim() ? `from ${b.date?.split(",")[1]?.trim()}` : ''}
                            {b.endDate ? ` to ${b.endDate.split(",")[1]?.trim() || '-'}` : ''}
                          </span>
                          <span style={{ color: brand.white }}>{b.items} players</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-2 gap-1 sm:gap-0">
                          <span className="text-base font-bold" style={{ color: brand.blueGray }}>{b.total}</span>
                          <span className="text-xs" style={{ color: brand.blueGray }}>Ref: {b.reference || '-'}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
            {/* Modal for booking details */}
            {selected && (
              <>
                {/* Overlay */}
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    zIndex: 9999,
                    background: 'rgba(0,0,0,0.6)',
                  }}
                />
                {/* Modal */}
                <div
                  style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10000,
                    width: '100%',
                    maxWidth: '420px',
                    padding: '1.5rem',
                  }}
                >
                  <div
                    className="rounded-xl xs:rounded-2xl shadow-2xl w-full flex flex-col gap-4 animate-fade-in relative"
                    style={{
                      backgroundColor: brand.nearBlack,
                      border: `1px solid ${brand.blueGray}55`,
                      maxHeight: '90vh',
                      overflowY: 'auto',
                    }}
                  >
                    <button className="absolute top-2 right-2 text-2xl font-bold" style={{ color: brand.blueGray }} onClick={() => setSelected(null)}>&times;</button>
                    <h3 className="text-xl xs:text-2xl font-bold mb-2" style={{ color: brand.blueGray }}>Booking Details</h3>
                    <div className="flex flex-col gap-2 text-sm xs:text-base">
                      <div><span className="font-semibold" style={{ color: brand.blueGray }}>Date:</span> <span style={{ color: brand.white }}>{selected.date}</span></div>
                      <div><span className="font-semibold" style={{ color: brand.blueGray }}>Time:</span> <span style={{ color: brand.blueGray }}>{selected.date?.split(",")[1]?.trim() ? `from ${selected.date?.split(",")[1]?.trim()}` : ''}{selected.endDate ? ` to ${selected.endDate.split(",")[1]?.trim() || '-'}` : ''}</span></div>
                      <div><span className="font-semibold" style={{ color: brand.blueGray }}>Players:</span> <span style={{ color: brand.white }}>{selected.items}</span></div>
                      <div><span className="font-semibold" style={{ color: brand.blueGray }}>Type:</span> <span style={{ color: brand.white }}>{selected.tipo?.charAt(0).toUpperCase() + selected.tipo?.slice(1)}</span></div>
                      <div><span className="font-semibold" style={{ color: brand.blueGray }}>Status:</span> <span className={
                        selected.state === 'ordered' ? 'text-yellow-400' :
                        selected.state === 'delivered' ? 'text-green-400' :
                        selected.state === 'cancelled' ? 'text-red-400' : 'text-[#B6F0FF]'
                      }>{selected.state === 'ordered' ? 'Upcoming' : selected.state === 'delivered' ? 'Attended' : selected.state === 'cancelled' ? 'Cancelled' : '-'}</span></div>
                      <div><span className="font-semibold" style={{ color: brand.blueGray }}>Total:</span> <span style={{ color: brand.white }}>{selected.total}</span></div>
                      <div><span className="font-semibold" style={{ color: brand.blueGray }}>Reference:</span> <span style={{ color: brand.blueGray }}>{selected.reference || '-'}</span></div>
                      <div><span className="font-semibold" style={{ color: brand.blueGray }}>Client Name:</span> <span style={{ color: brand.white }}>{selected.client}</span></div>
                      <div><span className="font-semibold" style={{ color: brand.blueGray }}>Email:</span> <span style={{ color: brand.blueGray }}>{selected.email}</span></div>
                      <div><span className="font-semibold" style={{ color: brand.blueGray }}>Phone:</span> <span style={{ color: brand.blueGray }}>{selected.address}</span></div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>

      {/* Modal completamente fuera del contenedor principal */}
      {selected && (
        <>
          {/* Overlay que cubre toda la pantalla */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 9999,
            }}
            onClick={() => setSelected(null)}
          />
          
          {/* Modal centrado en la pantalla */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10000,
              maxWidth: '90vw',
              width: '420px',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div
              className="rounded-xl xs:rounded-2xl shadow-2xl p-4 xs:p-6 flex flex-col gap-4 animate-fade-in relative"
              style={{
                backgroundColor: brand.nearBlack,
                border: `1px solid ${brand.blueGray}55`,
              }}
            >
              <button 
                className="absolute top-2 right-2 text-2xl font-bold hover:opacity-70 transition-opacity" 
                style={{ color: brand.blueGray }} 
                onClick={() => setSelected(null)}
              >
                &times;
              </button>
              
              <h3 className="text-xl xs:text-2xl font-bold mb-2 pr-8" style={{ color: brand.white }}>
                Booking Details
              </h3>
              
              <div className="flex flex-col gap-3 text-sm xs:text-base">
                <div><span className="font-semibold" style={{ color: brand.blueGray }}>Date:</span> <span style={{ color: brand.white }}>{selected.date}</span></div>
                <div><span className="font-semibold" style={{ color: brand.blueGray }}>Time:</span> <span style={{ color: brand.blueGray }}>{selected.date?.split(",")[1]?.trim() ? `from ${selected.date?.split(",")[1]?.trim()}` : ''}{selected.endDate ? ` to ${selected.endDate.split(",")[1]?.trim() || '-'}` : ''}</span></div>
                <div><span className="font-semibold" style={{ color: brand.blueGray }}>Players:</span> <span style={{ color: brand.white }}>{selected.items}</span></div>
                <div><span className="font-semibold" style={{ color: brand.blueGray }}>Type:</span> <span style={{ color: brand.white }}>{selected.tipo?.charAt(0).toUpperCase() + selected.tipo?.slice(1)}</span></div>
                <div><span className="font-semibold" style={{ color: brand.blueGray }}>Status:</span> <span className={
                  selected.state === 'ordered' ? 'text-yellow-400' :
                  selected.state === 'delivered' ? 'text-green-400' :
                  selected.state === 'cancelled' ? 'text-red-400' : 'text-[#B6F0FF]'
                }>{selected.state === 'ordered' ? 'Upcoming' : selected.state === 'delivered' ? 'Attended' : selected.state === 'cancelled' ? 'Cancelled' : '-'}</span></div>
                <div><span className="font-semibold" style={{ color: brand.blueGray }}>Total:</span> <span style={{ color: brand.white }}>{selected.total}</span></div>
                <div><span className="font-semibold" style={{ color: brand.blueGray }}>Reference:</span> <span style={{ color: brand.blueGray }}>{selected.reference || '-'}</span></div>
                <div><span className="font-semibold" style={{ color: brand.blueGray }}>Client Name:</span> <span style={{ color: brand.white }}>{selected.client}</span></div>
                <div><span className="font-semibold" style={{ color: brand.blueGray }}>Email:</span> <span style={{ color: brand.blueGray }}>{selected.email}</span></div>
                <div><span className="font-semibold" style={{ color: brand.blueGray }}>Phone:</span> <span style={{ color: brand.blueGray }}>{selected.address}</span></div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BookingHistory;
